import * as React from 'react';
import { useHits } from 'react-instantsearch';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Label } from 'recharts';
import NvChartTooltip from './NvChartTooltip';
import { buildData, Config } from "../helpers";
import { LinesPreloader } from "./Preloader";

export default function NvChartHitsWidget(props) {
  const {
    chartConfig,
    lineStyle = Config.Styles.Chart.DefaultLineStyle
  } = props;

  const [chartData, setChartData] = React.useState([]);
  const [chartGroups, setChartGroups] = React.useState([]);

  const hitsData = useHits(props);
  const {items} = hitsData;

  React.useEffect(() => {
    const {data, groups} = buildData(items, chartConfig);
    setChartData(data);
    setChartGroups(groups);
  }, [items]);

  if (chartData.length === 0 || chartGroups.length === 0) {
    return <LinesPreloader/>
  }

  const tickFormatter = (value, format) => value;

  const markerFillTypes = {
    filled: 'filled',
    empty: 'empty',
  };

  const colorsMap = {};
  const lineStylesMap = {}
  const markersMap = {};
  const markerFillMap = {};
  chartConfig.colorsMap.forEach(entry => {
    colorsMap[entry.id] = entry.color;
    lineStylesMap[entry.id] = entry.lineStyle;
    markersMap[entry.id] = entry.markerShape;
    markerFillMap[entry.id] = entry.markerFill || markerFillTypes.filled;
  });

  const lineStyles = {
    solid: null,
    dashed: "6 4",
    dotted: "2 2",
  };

  const markerRenderers = {
    circle: ({ cx, cy, stroke, index, isFilled }) => (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={isFilled ? stroke : 'white'}
        stroke={stroke}
        strokeWidth={2}
        key={`circle-${index}`}
      />
    ),

    square: ({ cx, cy, stroke, index, isFilled }) => (
      <rect
        x={cx - 4}
        y={cy - 4}
        width={8}
        height={8}
        fill={isFilled ? stroke : 'white'}
        stroke={stroke}
        strokeWidth={2}
        key={`square-${index}`}
      />
    ),

    triangle: ({ cx, cy, stroke, index, isFilled }) => (
      <polygon
        key={`triangle-${index}`}
        points={`${cx},${cy - 5} ${cx + 5},${cy + 5} ${cx - 5},${cy + 5}`}
        fill={isFilled ? stroke : 'white'}
        stroke={stroke}
        strokeWidth={2}
      />
    ),

    diamond: ({ cx, cy, stroke, index, isFilled }) => (
      <polygon
        key={`diamond-${index}`}
        points={`${cx},${cy - 5} ${cx + 5},${cy} ${cx},${cy + 5} ${cx - 5},${cy}`}
        fill={isFilled ? stroke : 'white'}
        stroke={stroke}
        strokeWidth={2}
      />
    ),

    cross: ({ cx, cy, stroke, index }) => (
      <g key={`cross-${index}`} stroke={stroke} strokeWidth={2}>
        <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy}/>
        <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5}/>
      </g>
    ),
  };

  const renderLegend = (props) => {
    const { payload } = props;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
        {payload.map((entry, index) => {
          const gid = entry.dataKey.toLowerCase();

          // Get the line style
          let lineStyleDash = null;
          Object.entries(lineStylesMap).forEach(val => {
            const [k, v] = val;
            if (gid.startsWith(k.toLowerCase())) {
              lineStyleDash = lineStyles[v];
            }
          });

          // Get the marker shape
          let markerShapeName = 'circle';
          Object.entries(markersMap).forEach(val => {
            const [k, v] = val;
            if (gid.startsWith(k.toLowerCase())) {
              if (markerRenderers[v]) {
                markerShapeName = v;
              }
            }
          });

          // Get the marker fill
          let isFilled = true;
          Object.entries(markerFillMap).forEach(val => {
            const [k, v] = val;
            if (gid.startsWith(k.toLowerCase())) {
              isFilled = v === markerFillTypes.filled;
            }
          });

          return (
            <div
              key={`legend-item-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '20px',
                marginBottom: '5px',
              }}
            >
              <svg width="40" height="20" style={{ marginRight: '5px' }}>
                {/* Line */}
                <line
                  x1="0"
                  y1="10"
                  x2="30"
                  y2="10"
                  stroke={entry.color}
                  strokeWidth="2"
                  strokeDasharray={lineStyleDash}
                />
                {/* Marker */}
                {markerShapeName === 'circle' && (
                  <circle
                    cx="15"
                    cy="10"
                    r="4"
                    fill={isFilled ? entry.color : 'white'}
                    stroke={entry.color}
                    strokeWidth="2"
                  />
                )}
                {markerShapeName === 'square' && (
                  <rect
                    x="11"
                    y="6"
                    width="8"
                    height="8"
                    fill={isFilled ? entry.color : 'white'}
                    stroke={entry.color}
                    strokeWidth="2"
                  />
                )}
                {markerShapeName === 'triangle' && (
                  <polygon
                    points="15,5 20,15 10,15"
                    fill={isFilled ? entry.color : 'white'}
                    stroke={entry.color}
                    strokeWidth="2"
                  />
                )}
                {markerShapeName === 'diamond' && (
                  <polygon
                    points="15,5 20,10 15,15 10,10"
                    fill={isFilled ? entry.color : 'white'}
                    stroke={entry.color}
                    strokeWidth="2"
                  />
                )}
                {markerShapeName === 'cross' && (
                  <g stroke={entry.color} strokeWidth="2">
                    <line x1="10" y1="10" x2="20" y2="10" />
                    <line x1="15" y1="5" x2="15" y2="15" />
                  </g>
                )}
              </svg>
              <span style={{ fontSize: '14px' }}>{entry.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <ResponsiveContainer width={Config.Styles.Chart.Width} height={Config.Styles.Chart.Height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3"/>
          <XAxis
            type="number"
            tickFormatter={(value) => tickFormatter(value, chartConfig.axes.x.format)}
            dataKey={chartConfig.axes.x.field}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.x.label} position="bottom" offset={0}/>
          </XAxis>
          <YAxis
            type="number"
            tickFormatter={(value) => tickFormatter(value, chartConfig.axes.y.format)}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.y.label} angle={-90} position="inside" dx={-35}/>
          </YAxis>
          <Tooltip content={<NvChartTooltip chartConfig={chartConfig}/>}/>
          <Legend content={renderLegend}/>

          {chartGroups.map((g, idx) => {
            const gid = g.id.toLowerCase();

            let color = "#000";
            Object.entries(colorsMap).forEach(val => {
              const [k, v] = val;
              if (gid.startsWith(k.toLowerCase())) {
                color = v;
              }
            });
            let lineStyle = null;
            Object.entries(lineStylesMap).forEach(val => {
              const [k, v] = val;
              if (gid.startsWith(k.toLowerCase())) {
                lineStyle = lineStyles[v];
              }
            });
            let markerShape = markerRenderers.circle;
            Object.entries(markersMap).forEach(val => {
              const [k, v] = val;
              if (gid.startsWith(k.toLowerCase())) {
                if(markerRenderers[v]){
                  markerShape = markerRenderers[v];
                }
              }
            });
            let isFilled = true;
            Object.entries(markerFillMap).forEach(val => {
              const [k, v] = val;
              if (gid.startsWith(k.toLowerCase())) {
                isFilled = v === markerFillTypes.filled;
              }
            });

            return (
              <Line
                key={g.id}
                dataKey={g.id}
                name={g.label}
                connectNulls={true}
                type={lineStyle}
                strokeWidth={2}
                stroke={color}
                strokeDasharray={lineStyle}
                dot={
                  ({ value, ...p }) => value === null ? null : markerShape({...p, isFilled})
                }
                activeDot={null}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
